/**
 * Checkbox primitive — a native `<input type="checkbox">` (free keyboard +
 * screen-reader semantics) with a required visible label. Controlled
 * (`checked` + `onChange`), token-driven, supports the visual `indeterminate`
 * state.
 */
import type { JSX } from 'solid-js';
import { createEffect } from 'solid-js';
import { ensureBaseStyles } from '../internal/base-styles.ts';
import { cx } from '../internal/cx.ts';
import { ensureStyles } from '../internal/inject-styles.ts';

const STYLE_ID = 'f4-checkbox';
const CSS = `
.f4-checkbox {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  cursor: pointer;
}
.f4-checkbox__input {
  width: var(--space-4);
  height: var(--space-4);
  accent-color: var(--color-accent);
}
.f4-checkbox--disabled { opacity: 0.6; cursor: not-allowed; }
.f4-checkbox__label { color: var(--color-text); font-size: var(--font-size-sm); }
`;

export interface CheckboxProps {
  /** Current state. */
  readonly checked: boolean;
  /** Emitted with the next state on toggle. */
  readonly onChange: (checked: boolean) => void;
  /** Visible label text (required — no bare literal). */
  readonly label: string;
  /** Visually indicates a "mixed" state without changing `checked`. */
  readonly indeterminate?: boolean;
  readonly disabled?: boolean;
  readonly id?: string;
  readonly class?: string;
}

export function Checkbox(props: CheckboxProps): JSX.Element {
  ensureBaseStyles();
  ensureStyles(STYLE_ID, CSS);
  let ref: HTMLInputElement | undefined;

  createEffect(() => {
    if (ref) ref.indeterminate = props.indeterminate ?? false;
  });

  return (
    <label
      class={cx('f4-checkbox', props.disabled && 'f4-checkbox--disabled', props.class)}
      for={props.id}
    >
      <input
        ref={ref}
        id={props.id}
        type="checkbox"
        class="f4-checkbox__input f4-focus-ring"
        checked={props.checked}
        disabled={props.disabled}
        onChange={(event) => props.onChange(event.currentTarget.checked)}
      />
      <span class="f4-checkbox__label">{props.label}</span>
    </label>
  );
}
